import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary, Icon } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { formatLPA } from '@kollez/shared';

/**
 * /group/colleges/:id — direct port of kollez/pages/group/college.html.
 *   5-KPI row + tabs (Departments / Programmes / Placement cell / Compliance).
 *   Now hits `/api/v1/group/colleges/:id` for the real record + department rollup.
 */

interface Department {
  _id: string;
  name: string;
  headOfDepartment?: { fullName?: string } | string;
  studentCount?: { total?: number };
}
interface CollegeDoc {
  _id: string;
  name: string;
  code?: string;
  type?: string;
  city?: string;
  address?: { city?: string; state?: string };
  affiliatedUniversity?: string;
  accreditations?: Array<{ body: string; grade?: string; validTill?: string }>;
  principal?: string | { name?: string };
  totalStudents?: number;
  totalFaculty?: number;
  programs?: Array<{ programCode?: string; specializations?: Array<{ code?: string }> }>;
}

type Tab = 'dept' | 'prog' | 'placement' | 'cell' | 'comp';

interface PlacementStats {
  collegeId: string;
  batchYear: number | null;
  totalStudents: number;
  applied: number;
  offered: number;
  accepted: number;
  placementRatePct: number;
  medianCTCInr: number | null;
  maxCTCInr: number | null;
  topHirers: Array<{ partnerId: string; name: string; offers: number }>;
}

interface DepartmentRollupRow {
  _id: string;
  name?: string;
  code?: string;
  totalStudents: number;
  applied: number;
  offered: number;
  accepted: number;
  placementRatePct: number;
  topSkills: Array<{ name: string; count: number }>;
}

function pickPrincipal(p: CollegeDoc['principal']): string {
  if (!p) return '—';
  if (typeof p === 'string') return p;
  return p.name ?? '—';
}

export function GroupCollegeDetailPage() {
  const { id } = useParams();
  const [tab, setTab] = useState<Tab>('dept');

  const query = useQuery({
    queryKey: ['group', 'college', id],
    queryFn: () => apiGet<{ college: CollegeDoc; students: number; departments: Department[] }>(`/group/colleges/${id}`),
    enabled: !!id,
  });

  const placementQ = useQuery({
    queryKey: ['group', 'college', id, 'placement-stats'],
    queryFn: () => apiGet<PlacementStats>(`/group/colleges/${id}/placement-stats`),
    enabled: !!id && tab === 'placement',
  });

  const deptRollupQ = useQuery({
    queryKey: ['group', 'college', id, 'departments-rollup'],
    queryFn: () => apiGet<{ collegeId: string; departments: DepartmentRollupRow[] }>(`/group/colleges/${id}/departments-rollup`),
    enabled: !!id && tab === 'dept',
  });

  const college = query.data?.college;
  const students = query.data?.students ?? 0;
  const departments = query.data?.departments ?? [];
  const initials = (college?.name ?? 'X').split(/\s+/).map((s) => s[0]).join('').slice(0, 3).toUpperCase();
  const naac = college?.accreditations?.find((a) => a.body === 'NAAC');

  return (
    <div className="stack">
      <div className="page-header">
        <div className="flex items-center gap-4 min-w-0">
          <span className="logo-tile" style={{ width: 56, height: 56, fontSize: '1rem' }}>{initials}</span>
          <div className="ph-text">
            <p className="eyebrow">
              {college?.type ?? 'Institution'}
              {college?.affiliatedUniversity ? ` · ${college.affiliatedUniversity}` : ''}
              {college?.code ? ` · ${college.code}` : ''}
              {naac ? ` · NAAC ${naac.grade}` : ''}
            </p>
            <h1 className="h-page">{college?.name ?? 'College'}</h1>
            <p className="subhead">
              {college?.address?.city ?? college?.city ?? '—'} · Principal {pickPrincipal(college?.principal)}
            </p>
          </div>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/principal/overview"><Icon name="eye" size={14} />College view</Link>
          <Link className="btn btn-ghost btn-icon" to="/group/structure" aria-label="Edit"><Icon name="pencil" size={14} /></Link>
        </div>
      </div>

      <AsyncBoundary kind="detail" loading={query.isLoading} error={query.error}>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
          <div className="card kpi"><span className="kpi-label">Students</span><span className="kpi-value">{students.toLocaleString('en-IN')}</span>{college?.totalFaculty ? <span className="kpi-meta">{college.totalFaculty} faculty</span> : null}</div>
          <div className="card kpi"><span className="kpi-label">Departments</span><span className="kpi-value">{departments.length}</span></div>
          <div className="card kpi"><span className="kpi-label">Programmes</span><span className="kpi-value">{college?.programs?.length ?? 0}</span></div>
          <div className="card kpi"><span className="kpi-label">Type</span><span className="kpi-value">{college?.type ?? '—'}</span></div>
          <div className="card kpi"><span className="kpi-label">NAAC</span><span className="kpi-value">{naac?.grade ?? '—'}</span>{naac?.validTill ? <span className="kpi-meta">valid to {new Date(naac.validTill).getFullYear()}</span> : null}</div>
        </div>

        <div className="tabs" role="tablist">
          {([['dept', 'Departments'], ['prog', 'Programmes'], ['placement', 'Placement stats'], ['cell', 'Placement cell'], ['comp', 'Compliance']] as const).map(([k, l]) => (
            <button key={k} className="tab" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>

        {tab === 'dept' && (
          <section className="card">
            {departments.length === 0 ? (
              <div className="empty-state p-8 text-center">
                <h3>No departments recorded yet</h3>
                <p className="cell-sub">Departments are added on the college's own dashboard.</p>
              </div>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th className="hide-mobile">Head</th>
                      <th className="r">Students</th>
                      <th className="r hide-mobile">Applied</th>
                      <th className="r">Accepted</th>
                      <th className="r">Rate</th>
                      <th className="hide-mobile">Top skills</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((d) => {
                      const roll = deptRollupQ.data?.departments.find((r) => r._id === d._id);
                      return (
                        <tr key={d._id}>
                          <td className="cell-title">{d.name}</td>
                          <td className="hide-mobile">{typeof d.headOfDepartment === 'object' ? d.headOfDepartment?.fullName ?? '—' : d.headOfDepartment ?? '—'}</td>
                          <td className="r">{(roll?.totalStudents ?? d.studentCount?.total ?? 0).toLocaleString('en-IN')}</td>
                          <td className="r hide-mobile">{roll ? roll.applied.toLocaleString('en-IN') : '—'}</td>
                          <td className="r">{roll ? roll.accepted.toLocaleString('en-IN') : '—'}</td>
                          <td className="r">
                            {roll ? (
                              <span className={`badge ${roll.placementRatePct >= 70 ? 'badge-success' : roll.placementRatePct >= 40 ? 'badge-warning' : ''}`}>
                                {roll.placementRatePct}%
                              </span>
                            ) : '—'}
                          </td>
                          <td className="hide-mobile cell-sub">
                            {roll?.topSkills.length ? roll.topSkills.map((s) => s.name).join(', ') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {deptRollupQ.isLoading && (
              <p className="cell-sub p-3 m-0">Loading department rollups…</p>
            )}
            <div className="card-foot"><button className="btn btn-ghost btn-sm"><Icon name="plus" size={14} />Add department</button></div>
          </section>
        )}

        {tab === 'placement' && (
          <section className="card card-pad">
            <AsyncBoundary
              kind="detail"
              inCard
              loading={placementQ.isLoading}
              error={placementQ.error}
              empty={!placementQ.isLoading && placementQ.data?.totalStudents === 0}
              emptyTitle="No students on roster yet"
              emptyHint="Placement stats appear once students are seeded for this college."
              onRetry={() => void placementQ.refetch()}
            >
              {placementQ.data && (
                <div className="stack">
                  <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
                    <div className="card kpi"><span className="kpi-label">Applied</span><span className="kpi-value">{placementQ.data.applied.toLocaleString('en-IN')}</span></div>
                    <div className="card kpi"><span className="kpi-label">Offered</span><span className="kpi-value">{placementQ.data.offered.toLocaleString('en-IN')}</span></div>
                    <div className="card kpi"><span className="kpi-label">Accepted</span><span className="kpi-value">{placementQ.data.accepted.toLocaleString('en-IN')}</span><span className="kpi-meta">{placementQ.data.placementRatePct}% rate</span></div>
                    <div className="card kpi">
                      <span className="kpi-label">Median CTC</span>
                      <span className="kpi-value">{placementQ.data.medianCTCInr ? formatLPA(placementQ.data.medianCTCInr) : '—'}</span>
                      {placementQ.data.maxCTCInr && <span className="kpi-meta">Max {formatLPA(placementQ.data.maxCTCInr)}</span>}
                    </div>
                  </div>

                  <div>
                    <h2 className="h-section">Top hirers</h2>
                    {placementQ.data.topHirers.length === 0 ? (
                      <p className="cell-sub m-0">No hires yet.</p>
                    ) : (
                      <div className="tbl-wrap">
                        <table className="tbl">
                          <thead><tr><th>Partner</th><th className="r">Accepted offers</th></tr></thead>
                          <tbody>
                            {placementQ.data.topHirers.map((h) => (
                              <tr key={h.partnerId}>
                                <td className="cell-title">
                                  <Link to={`/group/partners/${h.partnerId}`} className="link">{h.name}</Link>
                                </td>
                                <td className="r">{h.offers.toLocaleString('en-IN')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </AsyncBoundary>
          </section>
        )}

        {tab === 'prog' && (
          <section className="card">
            {(college?.programs ?? []).length === 0 ? (
              <div className="empty-state p-8 text-center">No programmes recorded</div>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead><tr><th>Programme</th><th className="r">Specialisations</th></tr></thead>
                  <tbody>
                    {(college?.programs ?? []).map((p, i) => (
                      <tr key={i}>
                        <td className="cell-title">{p.programCode ?? 'Programme'}</td>
                        <td className="r">{p.specializations?.length ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === 'cell' && (
          <section className="grid gap-5 lg:grid-cols-2">
            <div className="card card-pad grid gap-3">
              <h2 className="h-section">Policy inherited from your group</h2>
              <p className="helper m-0">Set at <Link className="link" to="/group/settings">group settings</Link>. This college can tighten, not relax.</p>
            </div>
            <div className="card card-pad grid gap-3">
              <h2 className="h-section">People</h2>
              <p className="helper m-0">Manage via <Link className="link" to="/group/team">team</Link>.</p>
            </div>
          </section>
        )}

        {tab === 'comp' && (
          <section className="card card-pad grid gap-2">
            {(college?.accreditations ?? []).length === 0 ? (
              <div className="empty-state p-8 text-center">No accreditation records</div>
            ) : (
              (college?.accreditations ?? []).map((a) => {
                const validTill = a.validTill ? new Date(a.validTill) : null;
                const expired = validTill && validTill < new Date();
                return (
                  <div key={a.body} className={`qcheck ${expired ? 'warn' : 'ok'}`}>
                    {expired ? <Icon name="circle-alert" size={14} /> : <Icon name="circle-check" size={14} />}
                    <span>
                      {a.body} {a.grade ?? ''} {validTill ? `· valid till ${validTill.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}` : ''}
                    </span>
                  </div>
                );
              })
            )}
            <Link className="link text-sm" to="/group/compliance">All colleges' compliance</Link>
          </section>
        )}
      </AsyncBoundary>
    </div>
  );
}
